// ─────────────────────────────────────────────────────────────────────────────
// Shared Layer 2 → ListingCard / HomeDetail shape adapter.
//
// Layer 2 (backend/services/doorlyListings.js) returns Doorly camelCase
// listings shaped for the Borrower API; the existing ListingCard JSX
// in Homes.js + the existing HomeDetail page were both built against
// the older MOCK_LISTINGS snake_case shape. This adapter bridges them
// so both consumers receive identical objects with the same field
// names — single source of truth for the on-card / on-detail shape.
//
// Both consumers import from here:
//   frontend/src/pages/Homes.js       — list grid (ListingCard)
//   frontend/src/pages/HomeDetail.js  — single listing detail
//
// CONTRACT
//   Input:  the Layer 2 normalized object exported by
//           getListing / getListingForBorrower / searchForBorrower
//           (see backend/services/anyprop.js normalizeListing).
//   Output: an object the existing card + detail JSX consume by
//           snake_case field names (sqft, year_built, hoa_fee,
//           property_type, address.street, photos[i]=URL string,
//           etc.), PLUS overlay fields (smartEstimate, uwAnnotation,
//           affordability) and the new Layer-1-additive fields
//           (description, lotSize) attached as additive extras.
//
// PHOTO MAPPING
//   Layer 2 returns photos as [{url, order, width, height}, ...]; the
//   card + detail JSX use listing.photos[i] as a URL string. We map
//   each non-null URL through and preserve the Order-sorted sequence.
//   When photos is empty (rare — gated by Layer 1's PhotosCount gt 0
//   floor but defensive), the JSX must show a "No photo available"
//   placeholder rather than a broken <img>.
//
// DAYS_ON_MARKET
//   Layer 2 carries modifiedAt; HAR doesn't expose a reliable
//   on-market date, so we compute days-since-modified as an
//   approximation. The number is informational, not precise.
// ─────────────────────────────────────────────────────────────────────────────

export function mapL2ToCardShape(l2) {
  if (!l2) return null;
  const dom = (() => {
    if (!l2.modifiedAt) return 0;
    const ms = Date.now() - new Date(l2.modifiedAt).getTime();
    return ms > 0 ? Math.floor(ms / 86400000) : 0;
  })();
  return {
    id:              l2.listingId,
    listingId:       l2.listingId,
    price:           l2.price,
    beds:            l2.beds,
    baths:           l2.baths,
    sqft:            l2.livingArea,
    property_type:   l2.propertySubType || l2.propertyType || 'Residential',
    year_built:      l2.yearBuilt,
    hoa_fee:         l2.hoaMonthly,
    days_on_market:  dom,
    is_promoted:     false,
    status:          l2.standardStatus || 'Active',
    // Layer 1 3b additions — HomeDetail needs these; the card may
    // surface description as a tooltip / hover snippet later.
    description:     l2.description || null,
    lot_size:        l2.lotSize     != null ? l2.lotSize : null,
    address: {
      street:        l2.address?.full
                       ? String(l2.address.full).split(',')[0].trim()
                       : '',
      city:          l2.address?.city  || '',
      state:         l2.address?.state || '',
      zip:           l2.address?.postalCode || '',
      full:          l2.address?.full   || '',
    },
    photos:          (l2.photos || []).map(p => p?.url).filter(Boolean),
    lat:             l2.lat,
    lng:             l2.lng,
    source:          l2.source,
    // Layer 2 overlay — surfaced for the card / detail to render.
    smartEstimate:   l2.smartEstimate || null,
    uwAnnotation:    l2.uwAnnotation  || null,
    affordability:   l2.affordability || null,
  };
}
